import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { Combobox } from '@headlessui/react';
import { Ref } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '@scale/scaleui';

export interface SimpleAutocompleteOption {
  id: string;
  title: string;
  icon?: FontAwesomeIconProps['icon'];
  metadata?: any;
}

type ComboboxProps = {
  value: SimpleAutocompleteOption;
  options: SimpleAutocompleteOption[];
  setSelectedOption: (option: SimpleAutocompleteOption) => void;
  onInputChange?: (text: string) => void;
  leadingIcon?: FontAwesomeIconProps['icon'];
  trailingIcon?: FontAwesomeIconProps['icon'];
  optionClassName?: string;
  inputProps?: Omit<React.ComponentProps<'input'>, 'ref'> & {
    ref?: Ref<HTMLInputElement> | undefined;
  };
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
};

const Autocomplete = ({
  options,
  setSelectedOption,
  className,
  disabled,
  value,
  leadingIcon,
  trailingIcon,
  onInputChange,
  optionClassName,
  inputProps,
  loading,
  placeholder,
}: ComboboxProps) => {
  return (
    <div className={twMerge('scaleui relative', className)}>
      <Combobox value={value} onChange={setSelectedOption} disabled={disabled}>
        {({ open }) => (
          <>
            <Combobox.Button
              as="div"
              className={twMerge(
                'relative w-full py-2 pl-3 pr-10 text-left border rounded-lg shadow-sm cursor-default focus:outline-none focus:ring-1 sm:text-sm bg-neutral-0 focus:ring-primary-500 border-neutral-300',
                disabled && 'cursor-not-allowed bg-neutral-50',
              )}
            >
              <div className="flex">
                {leadingIcon && (
                  <span className="pr-2 pointer-events-none">
                    <Icon icon={leadingIcon} />
                  </span>
                )}
                <Combobox.Input
                  {...inputProps}
                  className={twMerge(
                    'relative w-full p-0 shadow-none sm:text-sm cursor-default border-none focus:ring-0',
                    disabled
                      ? 'cursor-not-allowed bg-neutral-50 text-neutral-500'
                      : 'text-neutral-700',
                    inputProps?.className,
                  )}
                  displayValue={(item: any) => (item as SimpleAutocompleteOption).title}
                  onChange={event => onInputChange?.(event.target.value)}
                  placeholder={placeholder}
                />
                {trailingIcon && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <Icon icon={trailingIcon} />
                  </span>
                )}
              </div>
            </Combobox.Button>
            {open && (
              <Combobox.Options
                static
                className={
                  'absolute z-20 w-full py-1 mt-1 overflow-auto text-base rounded-lg shadow-lg bg-neutral-0 max-h-60 ring-1 ring-neutral-900 ring-opacity-5 focus:outline-none sm:text-sm dark:[color-scheme:dark]'
                }
              >
                {loading && (
                  <Combobox.Option key="loading" value="loading" disabled={true}>
                    {() => (
                      <p
                        className={twMerge(
                          'text-neutral-900 p-2 cursor-not-allowed bg-neutral-200',
                        )}
                      >
                        Loading...
                      </p>
                    )}
                  </Combobox.Option>
                )}
                {!loading && options.length === 0 && (
                  <Combobox.Option key="no-results" value="no-results" disabled={true}>
                    {() => (
                      <p
                        className={twMerge(
                          'text-neutral-900 p-2 cursor-not-allowed bg-neutral-200',
                        )}
                      >
                        No results
                      </p>
                    )}
                  </Combobox.Option>
                )}
                {options.map(option => (
                  <Combobox.Option
                    key={option.id}
                    value={option}
                    onClick={() => setSelectedOption(option)}
                    className={twMerge(
                      'relative select-none py-2 pl-3 pr-9 text-neutral-800 bg-neutral-0 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-200 group transition',
                      optionClassName,
                    )}
                  >
                    {({ active, selected }) => (
                      <>
                        {option.icon && (
                          <span className="absolute inset-y-0 left-0 flex items-center">
                            <Icon icon={option.icon} />
                          </span>
                        )}
                        <p
                          className={twMerge(
                            selected ? 'font-semibold' : 'font-normal',
                            'block truncate scale-90 origin-left transition-transform group-hover:scale-100',
                            option.icon ? 'pl-6' : '',
                            active ? 'text-clip' : 'text-truncate',
                          )}
                        >
                          {option.title}
                        </p>
                        {selected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                            <Icon icon={['fas', 'check']} variant="primary" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </>
        )}
      </Combobox>
    </div>
  );
};

export default Autocomplete;
